// "Set status": chooses a new lifecycle status for a version (valid_to required for superseded, optional for archived) and sets it under the document lock through POST /documents/{doc_uid}/status.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";

import type { DocumentStatus } from "@/api/__generated__";
import { lifecycleSetDocumentStatusMutation } from "@/api/__generated__/@tanstack/react-query.gen";
import { invalidateGraphData } from "@/api/keys";
import { Mono } from "@/components/common/Mono";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { notify } from "@/components/ui/sonner";
import { DateField } from "@/features/jobs/dialogs/DateField";
import { JobDialogFrame, JobDialogLayout } from "@/features/jobs/dialogs/JobDialog";
import { DOCUMENT_STATUS_LABELS } from "@/lib/labels";

const STATUSES: readonly DocumentStatus[] = ["draft", "published", "in_force", "superseded", "archived"];

const STATUS_HELP: Record<DocumentStatus, string> = {
  draft: "Under consultation; not applicable yet.",
  published: "Published but not yet in force.",
  in_force: "Applies now. Only one version of a family can be in force; use Promote to replace it.",
  superseded: "Replaced by a newer version from the end date.",
  archived: "Kept for reference only.",
};

interface SetStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  docUid: string;
  status: DocumentStatus;
}

export function SetStatusDialog({ open, onOpenChange, docUid, status }: SetStatusDialogProps) {
  return (
    <JobDialogFrame open={open} onOpenChange={onOpenChange} size="sm">
      <SetStatusForm
        docUid={docUid}
        status={status}
        onDone={() => {
          onOpenChange(false);
        }}
      />
    </JobDialogFrame>
  );
}

export function statusEndError(status: DocumentStatus, validTo: string): string | null {
  if (status === "superseded" && validTo.length === 0) {
    return "A superseded version needs the day its successor came into force.";
  }
  return null;
}

function SetStatusForm({
  docUid,
  status,
  onDone,
}: {
  docUid: string;
  status: DocumentStatus;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const choices = STATUSES.filter((item) => item !== status);
  const [next, setNext] = useState<DocumentStatus>(choices[0] ?? "published");
  const [validTo, setValidTo] = useState("");
  const legendId = useId();
  const needsEnd = next === "superseded" || next === "archived";
  const error = statusEndError(next, validTo);
  const update = useMutation({
    ...lifecycleSetDocumentStatusMutation(),
    onSuccess: (document) => {
      void invalidateGraphData(queryClient);
      notify(`${document.doc_uid} is ${DOCUMENT_STATUS_LABELS[document.status].toLowerCase()}`);
      onDone();
    },
  });
  return (
    <JobDialogLayout
      title="Set status"
      description={
        <>
          Changes the lifecycle status of <Mono tone="ink">{docUid}</Mono>, now{" "}
          {DOCUMENT_STATUS_LABELS[status].toLowerCase()}.
        </>
      }
      confirmLabel="Set status"
      canConfirm={error === null}
      pending={update.isPending}
      error={update.error}
      note="Runs under the document lock."
      onConfirm={() => {
        update.mutate({
          path: { doc_uid: docUid },
          body: { status: next, valid_to: needsEnd && validTo.length > 0 ? validTo : null },
        });
      }}
    >
      <div className="flex flex-col gap-2">
        <span id={legendId} className="text-chip font-medium text-ink-2">
          New status
        </span>
        <RadioGroup
          aria-labelledby={legendId}
          value={next}
          onValueChange={(value) => {
            setNext(value as DocumentStatus);
          }}
        >
          {choices.map((item) => (
            <StatusChoice key={item} status={item} />
          ))}
        </RadioGroup>
      </div>
      {needsEnd ? (
        <DateField
          label={next === "superseded" ? "Applies until" : "Applies until (optional)"}
          value={validTo}
          onChange={setValidTo}
          required={next === "superseded"}
          error={error}
          description="The first day the version no longer applies."
        />
      ) : null}
    </JobDialogLayout>
  );
}

function StatusChoice({ status }: { status: DocumentStatus }) {
  const id = useId();
  const helpId = useId();
  return (
    <div className="flex items-start gap-2.5">
      <RadioGroupItem id={id} value={status} aria-describedby={helpId} className="mt-0.5" />
      <div className="min-w-0">
        <Label htmlFor={id} className="text-ui text-foreground">
          {DOCUMENT_STATUS_LABELS[status]}
        </Label>
        <p id={helpId} className="m-0 mt-0.5 text-chip text-muted-foreground">
          {STATUS_HELP[status]}
        </p>
      </div>
    </div>
  );
}
